import React, { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { useWorkflowStore } from './store';
import {
  Database,
  FileText,
  Users,
  ThumbsUp,
  MessageSquare,
  Share2,
  Search,
  TrendingUp,
  UserPlus,
  Bell,
  Mail,
  Heart,
  Sparkles,
  Image,
  Type,
  Globe,
  Filter,
  Send,
  Eye,
  Layers,
  Zap,
  Activity,
  Repeat,
  MessageCircle,
  Download,
  Film,
  UploadCloud,
} from 'lucide-react';

// ── Icon mapping per node type ──────────────────────────────────────────────
export const NODE_ICONS: Record<string, React.ReactNode> = {
  brand_info: <Database size={20} />,
  seeding_contents_manager: <FileText size={20} />,
  account_selector: <Users size={20} />,
  filter_post: <Filter size={18} />,
  find_trend_fb: <TrendingUp size={18} />,
  comment_on_post: <MessageSquare size={18} />,
  like_posts: <ThumbsUp size={18} />,
  like_post_tiktok: <Heart size={18} />,
  share_post_tiktok: <Share2 size={18} />,
  comment_post_tiktok: <MessageSquare size={18} />,
  reply_comment_tiktok: <MessageSquare size={18} />,
  like_comment_tiktok: <ThumbsUp size={18} />,
  reply_to_comments: <MessageSquare size={18} />,
  like_pages: <Heart size={18} />,
  create_post_instagram: <Image size={18} className="text-pink-600" />,
  like_post_instagram: <Heart size={18} className="text-pink-600" />,
  repost_post_instagram: <Repeat size={18} className="text-pink-600" />,
  comment_post_instagram: <MessageCircle size={18} className="text-pink-600" />,
  follow_instagram: <UserPlus size={18} className="text-pink-600" />,
  reply_comment_instagram: <MessageSquare size={18} className="text-pink-600" />,
  create_post_threads: <Image size={18} className="text-zinc-800" />,
  like_post_threads: <Heart size={18} className="text-zinc-800" />,
  share_post_threads: <Repeat size={18} className="text-zinc-800" />,
  comment_post_threads: <MessageCircle size={18} className="text-zinc-800" />,
  follow_threads: <UserPlus size={18} className="text-zinc-800" />,
  find_join_groups: <Search size={18} />,
  find_group_posts: <Search size={18} />,
  find_top_posts: <TrendingUp size={18} />,
  post_to_group: <Send size={18} />,
  post_to_profile: <Send size={18} />,
  post_to_fanpage: <Send size={18} />,
  facebook_read_insight: <TrendingUp size={18} />,
  fanpage_comment_on_post: <MessageSquare size={18} />,
  fanpage_reply_to_comments: <MessageSquare size={18} />,
  fanpage_like_comments: <ThumbsUp size={18} />,
  fanpage_moderate_comments: <Eye size={18} />,
  fanpage_private_reply: <Mail size={18} />,
  share_url: <Share2 size={18} />,
  interact: <Activity size={18} />,
  check_message: <Mail size={18} />,
  view_notifications: <Bell size={18} />,
  accept_friend_requests: <UserPlus size={18} />,
  search_add_friend: <UserPlus size={18} />,
  ai_generate_asset: <Sparkles size={18} />,
  ai_remake_image: <Image size={18} />,
  ai_grok_remake_image: <Image size={18} />,
  ai_text_process: <Type size={18} />,
  search_groups: <Search size={18} />,
  join_groups: <Globe size={18} />,
  send_direct_message: <Send size={18} />,
  post_auto_responder: <MessageSquare size={18} />,
  group_random_messenger: <Users size={18} />,
  zalo_add_friend: <UserPlus size={18} />,
  zalo_join_group: <Globe size={18} />,
  zalo_group_members: <Users size={18} />,
  zalo_proactive_sender: <Send size={18} />,
  find_trend_omnichannel: <TrendingUp size={18} className="text-rose-600" />,
  download_video: <Download size={18} className="text-blue-600" />,
  analyze_viral_content: <Sparkles size={18} className="text-purple-600" />,
  auto_repurpose_video: <Film size={18} className="text-amber-600" />,
  vault_upload_file: <UploadCloud size={18} className="text-emerald-600" />,
};

// ── Category styling ────────────────────────────────────────────────────────
export const CATEGORY_STYLES: Record<string, { bg: string; border: string; iconBg: string; iconText: string; accent: string }> = {
  data: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    accent: 'bg-amber-400',
  },
  'with page': {
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    iconBg: 'bg-pink-100',
    iconText: 'text-pink-600',
    accent: 'bg-pink-400',
  },
  'with post': {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    accent: 'bg-blue-400',
  },
  'with group': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    accent: 'bg-emerald-400',
  },
  'with profile': {
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    iconBg: 'bg-violet-100',
    iconText: 'text-violet-600',
    accent: 'bg-violet-400',
  },
  ai_flow_action: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    iconBg: 'bg-teal-100',
    iconText: 'text-teal-600',
    accent: 'bg-teal-400',
  },
  control: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    iconBg: 'bg-gray-100',
    iconText: 'text-gray-600',
    accent: 'bg-gray-400',
  },
  action: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600',
    accent: 'bg-sky-400',
  },
  timing: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    iconBg: 'bg-orange-100',
    iconText: 'text-orange-600',
    accent: 'bg-orange-400',
  },
  messaging: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    accent: 'bg-rose-400',
  },
  'with tiktok': {
    bg: 'bg-red-50',
    border: 'border-red-300',
    iconBg: 'bg-red-100',
    iconText: 'text-red-600',
    accent: 'bg-red-500',
  },
  'with zalo': {
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    iconBg: 'bg-cyan-100',
    iconText: 'text-cyan-600',
    accent: 'bg-cyan-500',
  },
  'with instagram': {
    bg: 'bg-fuchsia-50',
    border: 'border-fuchsia-200',
    iconBg: 'bg-fuchsia-100',
    iconText: 'text-fuchsia-600',
    accent: 'bg-fuchsia-500',
  },
  'with threads': {
    bg: 'bg-zinc-50',
    border: 'border-zinc-300',
    iconBg: 'bg-zinc-200',
    iconText: 'text-zinc-800',
    accent: 'bg-zinc-600',
  },
  video_production: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    accent: 'bg-rose-500',
  },
};

const DEFAULT_STYLE = {
  bg: 'bg-white',
  border: 'border-gray-200',
  iconBg: 'bg-gray-100',
  iconText: 'text-gray-600',
  accent: 'bg-gray-400',
};

// ── Category labels ─────────────────────────────────────────────────────────
const CATEGORY_LABELS: Record<string, string> = {
  data: 'DATA',
  'with page': 'PAGE ACTION',
  'with post': 'POST ACTION',
  'with group': 'GROUP ACTION',
  'with profile': 'PROFILE ACTION',
  ai_flow_action: 'AI ACTION',
  control: 'CONTROL',
  action: 'ACTION',
  timing: 'TIMING',
  messaging: 'MESSAGING',
  'with tiktok': 'TIKTOK',
  'with zalo': 'ZALO ACTION',
  'with instagram': 'INSTAGRAM ACTION',
  'with threads': 'THREADS ACTION',
  video_production: 'VIDEO PRODUCTION',
};

// ── Helper to detect category from node type ────────────────────────────────
export const NODE_CATEGORY_MAP: Record<string, string> = {
  brand_info: 'data',
  seeding_contents_manager: 'data',
  account_selector: 'data',
  filter_post: 'with post',
  find_trend_fb: 'with post',
  comment_on_post: 'with post',
  like_posts: 'with post',
  like_post_tiktok: 'with tiktok',
  share_post_tiktok: 'with tiktok',
  comment_post_tiktok: 'with tiktok',
  reply_comment_tiktok: 'with tiktok',
  like_comment_tiktok: 'with tiktok',
  create_post_instagram: 'with instagram',
  like_post_instagram: 'with instagram',
  repost_post_instagram: 'with instagram',
  comment_post_instagram: 'with instagram',
  follow_instagram: 'with instagram',
  reply_comment_instagram: 'with instagram',
  create_post_threads: 'with threads',
  like_post_threads: 'with threads',
  share_post_threads: 'with threads',
  comment_post_threads: 'with threads',
  follow_threads: 'with threads',
  reply_to_comments: 'with post',
  like_pages: 'with page',
  search_groups: 'with group',
  find_join_groups: 'with group',
  find_group_posts: 'with group',
  find_top_posts: 'with post',
  join_groups: 'with group',
  post_to_group: 'with group',
  post_to_profile: 'with profile',
  post_to_fanpage: 'with page',
  facebook_read_insight: 'with page',
  fanpage_comment_on_post: 'with page',
  fanpage_reply_to_comments: 'with page',
  fanpage_like_comments: 'with page',
  fanpage_moderate_comments: 'with page',
  fanpage_private_reply: 'with page',
  share_url: 'with profile',
  interact: 'with profile',
  check_message: 'with profile',
  send_direct_message: 'messaging',
  post_auto_responder: 'messaging',
  group_random_messenger: 'messaging',
  view_notifications: 'with profile',
  accept_friend_requests: 'with profile',
  search_add_friend: 'with profile',
  ai_generate_asset: 'ai_flow_action',
  ai_remake_image: 'ai_flow_action',
  ai_grok_remake_image: 'ai_flow_action',
  zalo_add_friend: 'with zalo',
  zalo_join_group: 'with zalo',
  zalo_group_members: 'with zalo',
  zalo_proactive_sender: 'with zalo',
  find_trend_omnichannel: 'video_production',
  download_video: 'video_production',
  analyze_viral_content: 'video_production',
  auto_repurpose_video: 'video_production',
  vault_upload_file: 'video_production',
};

import DataZoneNode from './DataZoneNode';
import { DATA_NODE_TYPES } from './DataZoneNode';

// ── Config summary helpers ──────────────────────────────────────────────────
type ConfigItem = { label: string; value: string };

function getNodeConfig(data: any, nodeType: string): Record<string, any> {
  switch (nodeType) {
    case 'account_selector': return data.account_selector_config || data.account_selector || data.config || {};
    case 'brand_info': return data.brand_info_config || data.config || {};
    case 'seeding_contents_manager': return data.seeding_contents_manager_config || data.config || {};
    case 'post_to_profile': return data.post_to_profile_config || data.postToProfileConfig || data.config || {};
    case 'post_to_group': return data.post_to_group_config || data.postToGroupConfig || data.config || {};
    case 'post_to_fanpage': return data.post_to_fanpage_config || data.config || {};
    case 'facebook_read_insight': return data.facebook_read_insight_config || data.config || {};
    case 'fanpage_comment_on_post': return data.fanpage_comment_on_post_config || data.config || {};
    case 'fanpage_reply_to_comments': return data.fanpage_reply_to_comments_config || data.config || {};
    case 'fanpage_like_comments': return data.fanpage_like_comments_config || data.config || {};
    case 'fanpage_moderate_comments': return data.fanpage_moderate_comments_config || data.config || {};
    case 'fanpage_private_reply': return data.fanpage_private_reply_config || data.config || {};
    case 'comment_on_post': return data.comment_on_post_config || data.commentOnPostConfig || data.config || {};
    case 'reply_to_comments': return data.reply_to_comments_config || data.config || {};
    case 'filter_post': return data.filter_post_config || data.config || {};
    case 'find_trend_fb': return data.find_trend_fb_config || data.config || {};
    case 'share_url': return data.share_url_config || data.config || {};
    case 'search_groups': return data.search_groups_config || data.config || {};
    case 'join_groups': return data.join_groups_config || data.config || {};
    case 'like_pages': return data.like_pages_config || data.config || {};
    case 'like_posts': return data.like_posts_config || data.config || {};
    case 'like_post_tiktok': return data.like_post_tiktok_config || data.config || {};
    case 'share_post_tiktok': return data.share_post_tiktok_config || data.config || {};
    case 'like_post_instagram': return data.like_post_instagram_config || data.config || {};
    case 'create_post_instagram': return data.create_post_instagram_config || data.config || {};
    case 'repost_post_instagram': return data.repost_post_instagram_config || data.config || {};
    case 'comment_post_instagram': return data.comment_post_instagram_config || data.config || {};
    case 'follow_instagram': return data.follow_instagram_config || data.config || {};
    case 'reply_comment_instagram': return data.reply_comment_instagram_config || data.config || {};
    case 'like_post_threads': return data.like_post_threads_config || data.config || {};
    case 'create_post_threads': return data.create_post_threads_config || data.config || {};
    case 'share_post_threads': return data.share_post_threads_config || data.config || {};
    case 'comment_post_threads': return data.comment_post_threads_config || data.config || {};
    case 'follow_threads': return data.follow_threads_config || data.config || {};
    case 'comment_post_tiktok': return data.comment_post_tiktok_config || data.config || {};
    case 'reply_comment_tiktok': return data.reply_comment_tiktok_config || data.config || {};
    case 'like_comment_tiktok': return data.like_comment_tiktok_config || data.config || {};
    case 'interact': return data.interact_config || data.config || {};
    case 'find_join_groups': return data.find_join_groups_config || data.find_groups_config || data.find_groups || data.config || {};
    case 'find_group_posts': return data.find_group_posts_config || data.config || {};
    case 'find_top_posts': return data.find_top_posts_config || data.config || {};
    case 'ai_text_process': return data.ai_text_process_config || data.config || {};
    case 'ai_generate_asset': return data.ai_generate_asset_config || data.config || {};
    case 'ai_remake_image': return data.ai_remake_image_config || data.ai_grok_remake_image_config || data.config || {};
    case 'ai_grok_remake_image': return data.ai_grok_remake_image_config || data.ai_remake_image_config || data.config || {};
    case 'view_notifications': return data.view_notifications_config || data.config || {};
    case 'accept_friend_requests': return data.accept_friend_requests_config || data.config || {};
    case 'search_add_friend': return data.search_add_friend_config || data.config || {};
    case 'send_direct_message': return data.send_direct_message_config || data.config || {};
    case 'post_auto_responder': return data.post_auto_responder_config || data.config || {};
    case 'group_random_messenger': return data.group_random_messenger_config || data.config || {};
    case 'zalo_add_friend': return data.zalo_add_friend_config || data.config || {};
    case 'zalo_join_group': return data.zalo_join_group_config || data.config || {};
    case 'zalo_proactive_sender': return data.zalo_proactive_sender_config || data.config || {};
    case 'find_trend_omnichannel': return data.find_trend_omnichannel_config || data.config || {};
    case 'download_video': return data.download_video_config || data.config || {};
    case 'analyze_viral_content': return data.analyze_viral_content_config || data.config || {};
    case 'auto_repurpose_video': return data.auto_repurpose_video_config || data.config || {};
    case 'vault_upload_file': return data.vault_upload_file_config || data.config || {};
    default: return data.config || {};
  }
}

function truncStr(s: string, max = 20): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

function getConfigSummary(data: any, nodeType: string): ConfigItem[] {
  const cfg = getNodeConfig(data, nodeType);
  const items: ConfigItem[] = [];
  switch (nodeType) {
    case 'account_selector': {
      if (cfg.platform) items.push({ label: 'Platform', value: cfg.platform });
      const accs = cfg.selected_accounts || [];
      items.push({ label: 'Accounts', value: accs.length > 0 ? `${accs.length} selected` : 'None' });
      const wins = cfg.masterTimeline?.timeWindows;
      if (wins && wins.length > 0) {
        items.push({ label: 'Windows', value: wins.map((w: any) => `${w.start}–${w.end}`).join(', ') });
      } else if (cfg.masterTimeline?.activeHours) {
        items.push({ label: 'Active', value: `${cfg.masterTimeline.activeHours.start}–${cfg.masterTimeline.activeHours.end}` });
      }
      break;
    }
    case 'brand_info': {
      if (cfg.brand_name) items.push({ label: 'Brand', value: truncStr(cfg.brand_name) });
      if (cfg.industry) items.push({ label: 'Industry', value: truncStr(cfg.industry) });
      if (cfg.mention_style) items.push({ label: 'Style', value: cfg.mention_style });
      break;
    }
    case 'seeding_contents_manager': {
      if (cfg.name) items.push({ label: 'Name', value: truncStr(cfg.name) });
      const sa = cfg.seeding_accounts || [];
      items.push({ label: 'Accounts', value: sa.length > 0 ? `${sa.length} configured` : 'None' });
      break;
    }
    case 'post_to_profile': {
      if (cfg.content_type) items.push({ label: 'Content', value: cfg.content_type });
      if (cfg.image_source_type && cfg.image_source_type !== 'none') items.push({ label: 'Image', value: cfg.image_source_type });
      if (cfg.character_id) items.push({ label: 'Character', value: truncStr(cfg.character_id, 12) });
      if (cfg.add_to_seeding_contents) items.push({ label: 'Seeding', value: '✓' });
      break;
    }
    case 'post_to_group': {
      const groups = cfg.group_urls || [];
      items.push({ label: 'Groups', value: groups.length > 0 ? `${groups.length} URLs` : 'None' });
      if (cfg.image_source_type && cfg.image_source_type !== 'none') items.push({ label: 'Image', value: cfg.image_source_type });
      if (cfg.add_to_seeding_contents) items.push({ label: 'Seeding', value: '✓' });
      break;
    }
    case 'post_to_fanpage': {
      const pages = cfg.page_ids || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} page${pages.length > 1 ? 's' : ''}` : 'All pages' });
      if (cfg.content_type) items.push({ label: 'Content', value: cfg.content_type });
      if (cfg.add_to_seeding_contents) items.push({ label: 'Seeding', value: '✓' });
      if (cfg.schedule_mode === 'round_robin') items.push({ label: 'Mode', value: 'Round-robin' });
      break;
    }
    case 'facebook_read_insight': {
      const pages = cfg.page_ids || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} page${pages.length > 1 ? 's' : ''}` : 'All pages' });
      break;
    }
    case 'fanpage_comment_on_post': {
      const pages = cfg.page_ids || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} page${pages.length > 1 ? 's' : ''}` : 'All pages' });
      if (cfg.comment_mode) items.push({ label: 'Mode', value: cfg.comment_mode });
      if (cfg.selected_post_id) items.push({ label: 'Post', value: truncStr(cfg.selected_post_id, 18) });
      break;
    }
    case 'fanpage_reply_to_comments': {
      const pages = cfg.page_ids || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} page${pages.length > 1 ? 's' : ''}` : 'All pages' });
      if (cfg.reply_mode) items.push({ label: 'Reply', value: cfg.reply_mode });
      items.push({ label: 'Max/post', value: String(cfg.max_replies ?? cfg.max_replies_per_post ?? 2) });
      if (cfg.selected_post_id) items.push({ label: 'Post', value: truncStr(cfg.selected_post_id, 18) });
      break;
    }
    case 'fanpage_like_comments': {
      const pages = cfg.page_ids || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} page${pages.length > 1 ? 's' : ''}` : 'All pages' });
      items.push({ label: 'Filter', value: cfg.comment_filter_mode || 'all' });
      items.push({ label: 'Max', value: String(cfg.max_comments_to_like ?? 5) });
      if (cfg.selected_post_id) items.push({ label: 'Post', value: truncStr(cfg.selected_post_id, 18) });
      break;
    }
    case 'fanpage_moderate_comments': {
      const pages = cfg.page_ids || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} page${pages.length > 1 ? 's' : ''}` : 'All pages' });
      items.push({ label: 'Action', value: cfg.moderation_action || 'hide' });
      items.push({ label: 'Max', value: String(cfg.max_comments_to_moderate ?? 5) });
      if (cfg.selected_post_id) items.push({ label: 'Post', value: truncStr(cfg.selected_post_id, 18) });
      break;
    }
    case 'fanpage_private_reply': {
      const pages = cfg.page_ids || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} page${pages.length > 1 ? 's' : ''}` : 'All pages' });
      items.push({ label: 'Source', value: cfg.message_source_mode || 'ai' });
      items.push({ label: 'Max', value: String(cfg.max_messages_per_post ?? 3) });
      if (cfg.selected_post_id) items.push({ label: 'Post', value: truncStr(cfg.selected_post_id, 18) });
      break;
    }
    case 'comment_on_post': {
      if (cfg.comment_mode) items.push({ label: 'Mode', value: cfg.comment_mode });
      if (cfg.dispatch_mode) items.push({ label: 'Dispatch', value: cfg.dispatch_mode });
      if (cfg.seeding_mode) items.push({ label: 'Seeding', value: cfg.seeding_mode });
      if (cfg.dispatch_mode === 'specific' && cfg.post_url) items.push({ label: 'URL', value: truncStr(cfg.post_url, 18) });
      break;
    }
    case 'reply_to_comments': {
      if (cfg.dispatch_mode) items.push({ label: 'Dispatch', value: cfg.dispatch_mode });
      items.push({ label: 'Max replies', value: String(cfg.max_replies ?? 1) });
      if (cfg.dispatch_mode === 'specific' && cfg.post_url) items.push({ label: 'URL', value: truncStr(cfg.post_url, 18) });
      break;
    }
    case 'filter_post': {
      if (cfg.source_type) items.push({ label: 'Source', value: cfg.source_type });
      if (cfg.keyword) items.push({ label: 'Keyword', value: truncStr(cfg.keyword) });
      items.push({ label: 'Max', value: `${cfg.max_posts || 15} posts / ${cfg.max_days || 3}d` });
      break;
    }
    case 'find_trend_fb': {
      if (cfg.keyword) items.push({ label: 'Keyword', value: truncStr(cfg.keyword) });
      items.push({ label: 'Scrolls', value: String(cfg.max_scrolls || 5) });
      break;
    }
    case 'share_url': {
      if (cfg.url) items.push({ label: 'URL', value: truncStr(cfg.url, 25) });
      break;
    }
    case 'interact': {
      if (cfg.type) items.push({ label: 'Type', value: cfg.type });
      items.push({ label: 'Duration', value: `${cfg.duration_minutes || 5}m` });
      items.push({ label: '👍/🔁/💬', value: `${cfg.max_likes || 0}/${cfg.max_shares || 0}/${cfg.max_comments || 0}` });
      if (cfg.keywords && cfg.keywords.length > 0) items.push({ label: 'Keywords', value: truncStr(cfg.keywords.join(', '), 25) });
      break;
    }
    case 'find_join_groups': {
      const kws = cfg.keywords || [];
      if (kws.length) items.push({ label: 'Keywords', value: truncStr(kws.join(', ')) });
      items.push({ label: 'Max/week', value: String(cfg.max_groups_per_week || 5) });
      break;
    }
    case 'find_group_posts': {
      const groups = cfg.group_urls || [];
      items.push({ label: 'Groups', value: groups.length > 0 ? `${groups.length} URLs` : 'None' });
      if (cfg.search_mode) items.push({ label: 'Mode', value: cfg.search_mode });
      if (cfg.criteria) items.push({ label: 'Filter', value: truncStr(cfg.criteria) });
      items.push({ label: 'Days back', value: String(cfg.days_back ?? 0) });
      if (cfg.requirement && cfg.requirement !== 'default') items.push({ label: 'Tag', value: truncStr(cfg.requirement) });
      break;
    }
    case 'find_top_posts': {
      const urls = Array.isArray(cfg.source_urls)
        ? cfg.source_urls.filter((u: any) => typeof u === 'string' && u.trim())
        : (cfg.source_url ? [cfg.source_url] : []);
      if (urls.length > 0) items.push({ label: 'Sources', value: `${urls.length} URLs` });
      if (cfg.keyword) items.push({ label: 'Keyword', value: truncStr(cfg.keyword) });
      items.push({ label: 'Days', value: String(cfg.days_back ?? 7) });
      items.push({ label: 'Top', value: String(cfg.top_n ?? 1) });
      break;
    }
    case 'like_pages': {
      const pages = cfg.page_urls || [];
      items.push({ label: 'Pages', value: pages.length > 0 ? `${pages.length} URLs` : 'None' });
      items.push({ label: 'Max likes', value: String(cfg.max_likes || 3) });
      break;
    }
    case 'like_posts': {
      const urls = cfg.urls || [];
      items.push({ label: 'Posts', value: urls.length > 0 ? `${urls.length} URLs` : 'None' });
      break;
    }
    case 'create_post_instagram':
    case 'create_post_threads': {
      if (cfg.content_type) items.push({ label: 'Content', value: cfg.content_type });
      if (cfg.image_source_type && cfg.image_source_type !== 'none') items.push({ label: 'Image', value: cfg.image_source_type });
      break;
    }
    case 'like_post_tiktok':
    case 'share_post_tiktok':
    case 'like_post_instagram':
    case 'repost_post_instagram':
    case 'follow_instagram':
    case 'comment_post_instagram':
    case 'comment_post_tiktok':
    case 'like_comment_tiktok':
    case 'reply_comment_tiktok':
    case 'like_post_threads':
    case 'share_post_threads':
    case 'follow_threads':
    case 'comment_post_threads': {
      if (cfg.dispatch_mode) items.push({ label: 'Mode', value: cfg.dispatch_mode });
      if (cfg.dispatch_mode === 'urls') {
        const urls = cfg.urls || [];
        items.push({ label: 'URLs', value: urls.length > 0 ? `${urls.length}` : 'None' });
      } else if (cfg.dispatch_mode === 'seeding_for_account') {
        const ids = cfg.tiktok_ids || [];
        items.push({ label: 'Accounts', value: ids.length > 0 ? `${ids.length}` : 'None' });
        items.push({ label: 'Count', value: String(cfg.count ?? 10) });
      } else if (cfg.dispatch_mode === 'keyword_search') {
        if (cfg.keyword) items.push({ label: 'Keyword', value: truncStr(cfg.keyword) });
        items.push({ label: 'Count', value: String(cfg.count ?? 10) });
      }
      if (nodeType === 'comment_post_tiktok' && cfg.comment_mode) {
        items.push({ label: 'Comment', value: cfg.comment_mode });
      }
      if (nodeType === 'reply_comment_tiktok') {
        if (cfg.reply_mode) items.push({ label: 'Reply', value: cfg.reply_mode });
        if (cfg.comment_mode) items.push({ label: 'Content', value: cfg.comment_mode });
        if (cfg.max_replies) items.push({ label: 'Max', value: String(cfg.max_replies) });
      }
      if (nodeType === 'like_comment_tiktok') {
        if (cfg.target_mode) items.push({ label: 'Target', value: cfg.target_mode });
        if (cfg.max_likes) items.push({ label: 'Max', value: String(cfg.max_likes) });
      }
      break;
    }
    case 'reply_comment_instagram': {
      if (cfg.urls) items.push({ label: 'URLs', value: String(cfg.urls.length) });
      if (cfg.keywords) items.push({ label: 'Keywords', value: String(cfg.keywords.length) });
      if (cfg.max_comments) items.push({ label: 'Max comments', value: String(cfg.max_comments) });
      break;
    }
    case 'view_notifications': {
      items.push({ label: 'Max', value: String(cfg.max_notifications || 10) });
      if (cfg.mark_as_read) items.push({ label: 'Mark read', value: '✓' });
      break;
    }
    case 'accept_friend_requests': {
      items.push({ label: 'Max accepts', value: String(cfg.max_accepts || 10) });
      break;
    }
    case 'search_add_friend': {
      const kws = cfg.keywords || [];
      if (kws.length) items.push({ label: 'Keywords', value: truncStr(kws.join(', ')) });
      items.push({ label: 'Max add', value: String(cfg.max_add || 3) });
      break;
    }
    case 'ai_generate_asset': {
      if (cfg.task_type) items.push({ label: 'Task', value: cfg.task_type });
      break;
    }
    case 'ai_remake_image':
    case 'ai_grok_remake_image': {
      items.push({ label: 'Provider', value: cfg.ai_provider || 'grok' });
      items.push({ label: 'Task', value: (cfg.ai_provider === 'gemini') ? 'image.transform' : 'image.transform.grok' });
      if (cfg.model) items.push({ label: 'Model', value: truncStr(cfg.model) });
      break;
    }
    case 'ai_text_process': {
      if (cfg.task_type) items.push({ label: 'Task', value: cfg.task_type });
      if (cfg.input_source) items.push({ label: 'Input', value: cfg.input_source });
      break;
    }
    case 'send_direct_message': {
      const rcpts = cfg.recipients || [];
      const msgs = cfg.messages || [];
      items.push({ label: 'Recipients', value: rcpts.length > 0 ? `${rcpts.length}` : 'None' });
      items.push({ label: 'Messages', value: msgs.length > 0 ? `${msgs.length}` : 'None' });
      if (rcpts.length > 0 && msgs.length > 0) items.push({ label: 'Total sends', value: String(rcpts.length * msgs.length) });
      break;
    }
    case 'post_auto_responder': {
      if (cfg.post_url) items.push({ label: 'Post', value: truncStr(cfg.post_url, 22) });
      const kws = cfg.keywords || [];
      items.push({ label: 'Keywords', value: kws.length > 0 ? `${kws.length} terms` : 'defaults' });
      if (cfg.message) items.push({ label: 'DM', value: truncStr(cfg.message, 18) });
      break;
    }
    case 'group_random_messenger': {
      if (cfg.group_url) items.push({ label: 'Group', value: truncStr(cfg.group_url, 22) });
      items.push({ label: 'Limit', value: String(cfg.limit ?? 5) });
      if (cfg.message) items.push({ label: 'DM', value: truncStr(cfg.message, 18) });
      break;
    }
    case 'zalo_add_friend': {
      const phones = cfg.phone_numbers || [];
      items.push({ label: 'Targets', value: phones.length > 0 ? `${phones.length}` : 'None' });
      items.push({ label: 'Msg', value: truncStr(cfg.global_message || 'Mặc định', 15) });
      break;
    }
    case 'zalo_join_group': {
      if (cfg.invite_link) items.push({ label: 'Link', value: truncStr(cfg.invite_link, 22) });
      break;
    }
    case 'zalo_group_members': {
      if (cfg.group_id) items.push({ label: 'ID', value: truncStr(cfg.group_id, 15) });
      else if (cfg.group_name) items.push({ label: 'Name', value: truncStr(cfg.group_name, 15) });
      break;
    }
    case 'zalo_proactive_sender': {
      const rcpts = cfg.recipients || [];
      items.push({ label: 'Recipients', value: rcpts.length > 0 ? `${rcpts.length}` : 'None' });
      const imgs = cfg.global_images || [];
      if (imgs.length > 0) items.push({ label: 'Images', value: String(imgs.length) });
      break;
    }
    case 'find_trend_omnichannel': {
      if (cfg.platform) items.push({ label: 'Platform', value: cfg.platform });
      const kws = cfg.keywords || [];
      if (kws.length > 0) items.push({ label: 'Keywords', value: truncStr(kws.join(', '), 15) });
      if (cfg.max_results) items.push({ label: 'Max', value: String(cfg.max_results) });
      if (cfg.min_velocity_score) items.push({ label: 'Min Score', value: String(cfg.min_velocity_score) });
      break;
    }
    case 'download_video': {
      const urls = cfg.urls || [];
      items.push({ label: 'Source', value: urls.length > 0 ? `${urls.length} URLs` : 'Upstream context' });
      if (cfg.resolution) items.push({ label: 'Res', value: cfg.resolution });
      if (cfg.max_duration) items.push({ label: 'Max dur', value: `${cfg.max_duration}s` });
      break;
    }
    case 'analyze_viral_content': {
      if (cfg.brand_name) items.push({ label: 'Brand', value: truncStr(cfg.brand_name, 12) });
      if (cfg.target_audience) items.push({ label: 'Audience', value: truncStr(cfg.target_audience, 12) });
      if (cfg.target_language) items.push({ label: 'Lang', value: cfg.target_language });
      break;
    }
    case 'auto_repurpose_video': {
      if (cfg.target_aspect_ratio) items.push({ label: 'Ratio', value: cfg.target_aspect_ratio });
      if (cfg.voice) items.push({ label: 'Voice', value: truncStr(cfg.voice.replace('vi-VN-', ''), 10) });
      if (cfg.burn_subtitles !== undefined) items.push({ label: 'Subtitles', value: cfg.burn_subtitles ? 'Yes' : 'No' });
      break;
    }
    case 'vault_upload_file': {
      if (cfg.goal) items.push({ label: 'Goal', value: cfg.goal });
      if (cfg.title) items.push({ label: 'Title', value: truncStr(cfg.title, 14) });
      break;
    }
  }
  return items;
}

// ── Data node shape configs ─────────────────────────────────────────────────
const DATA_SHAPE_STYLES: Record<string, { fill: string; stroke: string; selectedStroke: string; textColor: string }> = {
  account_selector: { fill: '#fef3c7', stroke: '#f59e0b', selectedStroke: '#3b82f6', textColor: 'text-amber-700' },
  brand_info: { fill: '#dbeafe', stroke: '#3b82f6', selectedStroke: '#8b5cf6', textColor: 'text-blue-700' },
  seeding_contents_manager: { fill: '#dcfce7', stroke: '#22c55e', selectedStroke: '#3b82f6', textColor: 'text-emerald-700' },
};

// ── Shaped Data Node ────────────────────────────────────────────────────────
const DataShapedNode: React.FC<NodeProps> = ({ id, data, type, selected }) => {
  const nodeType = type || (data as any)?.type || '';
  const label = (data as any)?.label || 'Untitled';
  const icon = NODE_ICONS[nodeType] || <Layers size={20} />;
  const shapeStyle = DATA_SHAPE_STYLES[nodeType] || DATA_SHAPE_STYLES.account_selector;
  const isInvalid = useWorkflowStore((s) => s.invalidNodeIds.has(id));
  const strokeColor = isInvalid ? '#ef4444' : selected ? shapeStyle.selectedStroke : shapeStyle.stroke;
  const strokeWidth = selected ? 3 : 2;

  // Pulse animation for account_selector until configured or clicked
  const accountCfg = nodeType === 'account_selector' ? getNodeConfig(data, nodeType) : null;
  const isAccountConfigured = (accountCfg?.selected_accounts?.length ?? 0) > 0;
  const [showGuide, setShowGuide] = useState(nodeType === 'account_selector' && !isAccountConfigured);
  useEffect(() => {
    if (selected && showGuide) setShowGuide(false);
  }, [selected]);
  useEffect(() => {
    if (isAccountConfigured && showGuide) setShowGuide(false);
  }, [isAccountConfigured]);
  const isPulsing = showGuide && nodeType === 'account_selector' && !selected;

  const renderShape = () => {
    switch (nodeType) {
      case 'account_selector':
        return (
          <svg viewBox="0 0 100 90" className="w-full h-full">
            <polygon
              points="50,3 95,25 95,65 50,87 5,65 5,25"
              fill={shapeStyle.fill}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
            />
            {selected && (
              <polygon
                points="50,3 95,25 95,65 50,87 5,65 5,25"
                fill="none"
                stroke={shapeStyle.selectedStroke}
                strokeWidth={1}
                strokeDasharray="0"
                opacity={0.3}
                transform="translate(0,0) scale(1.05)"
                style={{ transformOrigin: '50px 45px' }}
              />
            )}
          </svg>
        );
      case 'brand_info':
        return (
          <svg viewBox="0 0 100 90" className="w-full h-full">
            <rect
              x="18" y="13" width="64" height="64" rx="10"
              fill={shapeStyle.fill}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              transform="rotate(45 50 45)"
            />
          </svg>
        );
      case 'seeding_contents_manager':
        return (
          <svg viewBox="0 0 100 90" className="w-full h-full">
            <circle
              cx="50" cy="45" r="42"
              fill={shapeStyle.fill}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const configItems = getConfigSummary(data, nodeType);

  return (
    <div className={`relative ${selected ? 'drop-shadow-lg' : 'drop-shadow-sm'} transition-all duration-200 ${isInvalid ? 'animate-pulse' : ''}`}>
      {/* Invalid indicator ring */}
      {isInvalid && (
        <div className="absolute -inset-2 z-0 rounded-full border-2 border-red-400 animate-ping opacity-40" />
      )}
      {isPulsing && (
        <div className="absolute -inset-3 z-0">
          <div className="w-full h-full rounded-full animate-ping bg-amber-300/30" />
        </div>
      )}
      {isPulsing && (
        <div className="absolute -inset-2 z-0 rounded-full animate-pulse border-2 border-amber-400/50" />
      )}
      {/* SVG Shape */}
      <div className="w-[100px] h-[90px] relative z-10">
        {renderShape()}
      </div>
      {/* Icon + Label overlay */}
      <div className="absolute top-0 left-0 w-[100px] h-[90px] flex flex-col items-center justify-center pointer-events-none z-10">
        <div className={shapeStyle.textColor}>{icon}</div>
        <span className={`text-[9px] font-bold ${shapeStyle.textColor} mt-0.5 text-center leading-tight px-2`}>
          {label}
        </span>
      </div>
      {/* Config summary card */}
      {configItems.length > 0 && (
        <div className={`mt-1 bg-white/90 backdrop-blur-sm border rounded-lg px-2 py-1.5 shadow-sm min-w-[120px] ${isInvalid ? 'border-red-300' : 'border-gray-200'}`}>
          {configItems.map((item, i) => (
            <div key={i} className="flex justify-between gap-2 text-[9px] leading-[14px]">
              <span className="text-gray-400 flex-shrink-0">{item.label}</span>
              <span className="text-gray-700 font-medium truncate text-right">{item.value}</span>
            </div>
          ))}
        </div>
      )}
      {/* Error badge */}
      {isInvalid && (
        <div className="absolute -top-2 -right-2 z-30 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-md">
          <span className="text-white text-[10px] font-bold">!</span>
        </div>
      )}
      {/* Guide tooltip */}
      {isPulsing && (
        <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 whitespace-nowrap z-20">
          <div className="bg-gray-900 text-white text-[10px] font-medium px-2.5 py-1 rounded-md shadow-lg animate-bounce">
            👆 Click to configure accounts
          </div>
        </div>
      )}

      {/* Output handle (bottom) – hidden for brand_info & seeding_contents_manager */}
      {nodeType !== 'brand_info' && nodeType !== 'seeding_contents_manager' && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="!w-3 !h-3 !rounded-full !border-2 !border-white !bg-rose-400 hover:!bg-rose-500 !-bottom-1.5"
        />
      )}
    </div>
  );
};

const WorkflowNode: React.FC<NodeProps> = ({ id, data, type, selected }) => {
  const nodeType = type || (data as any)?.type || '';
  const label = (data as any)?.label || 'Untitled';
  const category = NODE_CATEGORY_MAP[nodeType] || 'control';
  const style = CATEGORY_STYLES[category] || DEFAULT_STYLE;
  const icon = NODE_ICONS[nodeType] || <Layers size={18} />;
  const categoryLabel = CATEGORY_LABELS[category] || 'NODE';
  const configItems = getConfigSummary(data, nodeType);
  const isInvalid = useWorkflowStore((s) => s.invalidNodeIds.has(id));

  return (
    <div
      className={`
        relative min-w-[180px] max-w-[240px] rounded-xl border-2 shadow-sm
        transition-all duration-200
        ${isInvalid ? 'border-red-400 bg-red-50 animate-pulse' : `${style.bg} ${style.border}`}
        ${selected ? 'ring-2 ring-blue-400 ring-offset-1 shadow-md' : 'hover:shadow-md'}
      `}
    >
      {/* Error badge */}
      {isInvalid && (
        <div className="absolute -top-2 -right-2 z-30 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-md">
          <span className="text-white text-[10px] font-bold">!</span>
        </div>
      )}
      {/* Top accent bar */}
      <div className={`h-1 rounded-t-[10px] ${style.accent}`} />

      {/* Content */}
      <div className="px-3 py-2.5">
        {/* Header row: icon + label */}
        <div className="flex items-center gap-2 mb-1">
          <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${style.iconBg} ${style.iconText}`}>
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-gray-800 leading-tight truncate">
              {label}
            </div>
            <div className="text-[10px] font-medium tracking-wider text-gray-400 uppercase">
              {categoryLabel}
            </div>
          </div>
        </div>

        {/* Config summary */}
        {configItems.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200/60 space-y-0.5">
            {configItems.map((item, i) => (
              <div key={i} className="flex justify-between gap-2 text-[10px] leading-[15px]">
                <span className="text-gray-400 flex-shrink-0">{item.label}</span>
                <span className="text-gray-600 font-medium truncate text-right">{item.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !rounded-full !border-2 !border-white !bg-blue-400 hover:!bg-blue-500 !-top-1.5"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !rounded-full !border-2 !border-white !bg-rose-400 hover:!bg-rose-500 !-bottom-1.5"
      />
    </div>
  );
};

export default memo(WorkflowNode);

// ── Build nodeTypes map for ReactFlow ───────────────────────────────────────
const ALL_NODE_TYPE_KEYS = Object.keys(NODE_CATEGORY_MAP);

export const customNodeTypes: Record<string, React.ComponentType<any>> = {
  data_zone: DataZoneNode,
};
for (const key of ALL_NODE_TYPE_KEYS) {
  if (DATA_NODE_TYPES.includes(key)) {
    customNodeTypes[key] = DataShapedNode;
  } else {
    customNodeTypes[key] = WorkflowNode;
  }
}

